import type { FleetStatusRow } from '../../lib/database.types'
import { INBOX } from '../../app/routes'
import { clock } from '../../lib/time'
import { count } from './summaries'

/**
 * The fleet's own standing, as the `fleet-status` agent last wrote it.
 *
 * **This module does not compute health**, the same boundary `health.ts` holds
 * one level up. The agent decides whether a job is failed, overdue, waiting on
 * a person or fine, and writes a sentence about it; the panel reports that
 * verbatim and arranges it. Re-deriving "overdue" here from `next_due_at`
 * would be a second implementation of a judgement the fleet already made, and
 * the two would eventually disagree in front of the two people meant to trust
 * this screen.
 *
 * `fleet_status` is keyed by `job` and replaced whole every fifteen minutes, so
 * every row of a pass carries the same `generated_at`. That timestamp is the
 * page's own freshness rather than the fleet's: a status job that has stopped
 * running has to look different from a fleet with nothing wrong, which is the
 * same move the Directory makes with its `generated_at` and the cost strip
 * makes with the watchdog's heartbeat.
 */

/**
 * How old the newest pass may be before the panel stops claiming to know.
 *
 * The agent's timer is every fifteen minutes, so this is two missed passes --
 * a real outage rather than one slow run or a reboot, and the figure the
 * ticket names.
 */
export const STATUS_STALE_MS = 30 * 60 * 1000

/**
 * The one row that is not a scheduled job: the inbox's own standing, which the
 * agent publishes alongside the jobs so that this panel can state the queue
 * without a second read. It is lifted out of the job list into its own section
 * and out of the job counts, because it has no last run and no next due and
 * would render as a row of dashes among things that do.
 *
 * Matched by name rather than by `where_runs = 'fleet'`, and the difference is
 * which way it fails. A second fleet-level row added later -- open findings,
 * say -- would be wrongly promoted into the approval-queue section by the
 * column test, whereas if this name ever changes the row simply falls through
 * and renders as an ordinary row. Nothing is hidden either way round, and
 * being demoted to a visible row is the safer of the two.
 */
export const QUEUE_JOB = 'approvals'

/**
 * The statuses the table's own check constraint allows, in the order a person
 * should read them, with what to call one and which state colour it takes.
 *
 * `not_readable` is in the constraint and is not in the ticket's list of four:
 * it is how the agent says it could not see a job at all (today, Claude's
 * scheduled tasks are not readable from the box). It is neither broken nor
 * fine, so it reads as itself in the muted queued grey and sits at the bottom
 * of the attention list -- a known unknown is still something a person should
 * see, and folding it into either side would misreport it.
 *
 * A status not in this table renders as its own value and is treated as
 * needing attention, the way the activity feed renders an unregistered event
 * type rather than hiding it. Guessing "fine" about a word we do not know is
 * the one answer that loses information silently.
 */
const STATUSES: ReadonlyArray<{ value: string; label: string; token: string }> = [
  { value: 'failed', label: 'Failed', token: 'fail' },
  { value: 'overdue', label: 'Overdue', token: 'retry' },
  { value: 'needs_you', label: 'Needs you', token: 'wait' },
  { value: 'not_readable', label: 'Not readable', token: 'queue' },
  { value: 'ok', label: 'OK', token: 'done' },
]

/** Where a job runs, as the column's three allowed values are written out. */
const WHERE: Record<string, string> = {
  box: 'On the box',
  claude: 'In Claude',
  fleet: 'In the fleet',
}

export interface StatusJob {
  job: string
  /** The `where_runs` value written out, or the value itself if it is a new one. */
  where: string
  /** The raw column value, which is what an unrecognised status renders as. */
  status: string
  statusLabel: string
  /** A `--s-*` token name, for the one word of colour a row carries. */
  token: string
  note: string
  /** Empty strings are how the agent writes "no link", and read as none. */
  link: string | null
  lastRunAt: string | null
  /** The agent's word for how the last run ended -- `success`, `exit-code`. */
  lastResult: string | null
  nextDueAt: string | null
}

/** The inbox's standing, as the pass recorded it. */
export interface QueueStanding {
  /** The agent's own line: "45 waiting, oldest 40 days". Not parsed into a number. */
  note: string
  href: string
}

export interface StatusPanel {
  /** When the newest pass ran, or null if the table holds nothing at all. */
  generatedAt: string | null
  /** The newest pass is older than the agent's timer allows for. */
  stale: boolean
  /** "Status job down since 09:42", or null while it is reporting. */
  downSince: string | null
  /** Everything that is not OK, worst first. */
  attention: StatusJob[]
  /** Everything that is, by name. */
  clear: StatusJob[]
  /** The inbox row, lifted out of both lists above. Null if the pass had none. */
  queue: QueueStanding | null
  /** "Updated 09:42 · 2 need you · 0 failed · 27 OK". */
  headline: string
}

function known(status: string) {
  return STATUSES.find((s) => s.value === status) ?? null
}

function toJob(row: FleetStatusRow): StatusJob {
  const match = known(row.status)
  return {
    job: row.job,
    where: WHERE[row.where_runs] ?? row.where_runs,
    status: row.status,
    statusLabel: match?.label ?? row.status,
    token: match?.token ?? 'queue',
    note: row.note,
    link: row.link?.trim() ? row.link.trim() : null,
    lastRunAt: row.last_run_at,
    lastResult: row.last_result,
    nextDueAt: row.next_due_at,
  }
}

/** Worst first, in the registry's order; an unknown status sorts after all of them. */
function rank(status: string): number {
  const at = STATUSES.findIndex((s) => s.value === status)
  return at === -1 ? STATUSES.length : at
}

/**
 * The counts, in the order the ticket writes them.
 *
 * "need you" and "failed" are printed even at zero, because the whole claim of
 * the line is that it looked and found none -- a header that silently drops
 * them reads the same whether the count is nought or the panel is broken. The
 * statuses between them are printed only when they are not zero, so a fleet
 * with nothing overdue does not have to read a row of noughts to find that out
 * (`summaries.ts` holds the same rule for the lane group lines).
 */
function tally(jobs: ReadonlyArray<StatusJob>): string {
  const n = (value: string) => jobs.filter((j) => j.status === value).length
  const parts = [`${n('needs_you')} need you`, `${n('failed')} failed`]
  for (const s of STATUSES) {
    if (s.value === 'needs_you' || s.value === 'failed' || s.value === 'ok') continue
    const c = n(s.value)
    if (c > 0) parts.push(`${c} ${s.label.toLowerCase()}`)
  }
  // Anything the registry does not know, named by its own value rather than
  // swept into a total that would make it look accounted for.
  const unknown = new Map<string, number>()
  for (const j of jobs) if (!known(j.status)) unknown.set(j.status, (unknown.get(j.status) ?? 0) + 1)
  for (const [value, c] of unknown) parts.push(`${c} ${value}`)
  parts.push(`${n('ok')} OK`)
  return parts.join(' · ')
}

/**
 * @param rows  the latest pass, every row of it
 * @param now   the clock, injectable so staleness is testable
 */
export function toStatusPanel(rows: ReadonlyArray<FleetStatusRow>, now: number = Date.now()): StatusPanel {
  // The newest stamp rather than any row's: the table is replaced whole, so
  // they agree today, and if a partial write ever left them disagreeing the
  // freshest is the only one that could still be true.
  let generatedAt: string | null = null
  for (const r of rows) if (generatedAt === null || r.generated_at > generatedAt) generatedAt = r.generated_at

  const queueRow = rows.find((r) => r.job === QUEUE_JOB) ?? null
  const jobs = rows.filter((r) => r !== queueRow).map(toJob)

  const attention = jobs
    .filter((j) => j.status !== 'ok')
    .sort((a, b) => rank(a.status) - rank(b.status) || a.job.localeCompare(b.job))
  const clear = jobs.filter((j) => j.status === 'ok').sort((a, b) => a.job.localeCompare(b.job))

  const at = generatedAt ? Date.parse(generatedAt) : NaN
  // An empty table is day one, not an outage: before the agent's first pass
  // there is nothing to be stale, and saying "down" about a job that has never
  // run would be an assertion nothing supports. The view renders that case as
  // an empty panel instead.
  const stale = generatedAt !== null && (Number.isNaN(at) || now - at > STATUS_STALE_MS)

  return {
    generatedAt,
    stale,
    downSince: stale && generatedAt ? `Status job down since ${clock(generatedAt)}` : null,
    attention,
    clear,
    queue: queueRow ? { note: queueRow.note, href: INBOX } : null,
    headline: `${generatedAt ? `Updated ${clock(generatedAt)}` : 'Never updated'} · ${tally(jobs)}`,
  }
}

/** The closed All clear section's one line: "18 jobs, nothing to do about any of them". */
export function clearLine(jobs: ReadonlyArray<StatusJob>): string {
  return `${count(jobs.length, 'job')}, nothing to do about any of them`
}

/**
 * The panel as the Status view builds it, from a query result rather than from
 * rows -- which exists because *which clock* the staleness check reads is the
 * whole of DEV-308.
 *
 * `toStatusPanel`'s default clock is the moment it is called, and that is the
 * render clock. React Query's structural sharing hands back the same `data`
 * reference when a poll brings nothing new, so a stopped status job produces
 * polls that change nothing, no re-render, no recompute, and a banner that
 * never fires on the tab that needed it. `dataUpdatedAt` is restamped on every
 * successful fetch whether or not the rows moved, so it advances exactly when
 * the page has re-confirmed what it is showing -- which is the claim the
 * banner makes.
 *
 * A query that is not succeeding -- an offline tab, a failing read -- holds
 * `dataUpdatedAt` still and so holds the banner back. That is deliberate the
 * same way the empty table is: the page would be asserting the status job is
 * down when what it actually knows is that it cannot see.
 */
export function statusPanelFrom(
  read: { data?: ReadonlyArray<FleetStatusRow>; dataUpdatedAt: number },
): StatusPanel {
  return toStatusPanel(read.data ?? [], read.dataUpdatedAt || Date.now())
}
